import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Stack, Dialog, Button, Typography, DialogContent, DialogActions } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { enqueueSnackbar } from 'notistack';
import { useForm, Controller } from 'react-hook-form';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import { PositionTxType } from 'src/type/position';
import { TransactionType } from 'src/type/transaction';
import axios, { endpoints } from 'src/utils/axios';
import { toastError } from 'src/utils/format-error';
import dayjs, { Dayjs } from 'dayjs';
import * as Yup from 'yup';
import { txAction } from './utils';

type Props = {
  open: boolean;
  onClose: () => void;
  tx: PositionTxType;
  handleReload: () => void;
};

export default function PositionUpdateNoteDialog({ open, onClose, tx, handleReload }: Props) {
  const Schema = Yup.object().shape({
    note: Yup.string(),
    ticketNumber: Yup.string(),
    ticketCreatedAt: Yup.mixed<Dayjs>().nullable(),
  });

  const defaultValues = {
    note: tx.note || '',
    ticketNumber: tx.ticketNumber || '',
    ticketCreatedAt: tx.ticketCreatedAt ? dayjs(tx.ticketCreatedAt) : (null as unknown as Dayjs),
  };

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(Schema),
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    control,
    formState: { isSubmitting },
  } = methods;

  const beforeClose = () => {
    reset(defaultValues);
    onClose();
  };

  const onSubmit = handleSubmit(async (values) => {
    try {
      await axios.patch(endpoints.transactions.getById(tx.id), {
        note: values.note,
        ticketNumber: values.ticketNumber,
        ticketCreatedAt: values.ticketCreatedAt
          ? dayjs(values.ticketCreatedAt).toISOString()
          : null,
      });

      handleReload();
      beforeClose();
      enqueueSnackbar('อัพเดทข้อมูลสำเร็จ', { variant: 'success' });
    } catch (error) {
      toastError(error);
    }
  });

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={beforeClose}>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <Stack direction="row" alignItems="center" sx={{ p: 3 }}>
          <Typography
            color={tx.type === TransactionType.BUY_POSITION ? 'success.main' : 'error'}
            variant="h6"
            mx={0.5}
          >
            {txAction(tx.type)}
          </Typography>
          <Typography variant="h6">
            {tx.product.name} {tx.amount} {tx.unitName}
          </Typography>
        </Stack>

        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack spacing={2} direction={{ xs: 'column', md: 'row' }}>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
                <Controller
                  name="ticketCreatedAt"
                  control={control}
                  render={({ field, fieldState }) => (
                    <DatePicker
                      {...field}
                      label="วันที่ออกตั๋ว"
                      format="DD/MM/YYYY"
                      sx={{ width: 1 }}
                      slotProps={{
                        textField: {
                          error: Boolean(fieldState.error),
                          helperText: fieldState.error?.message,
                        },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>

              <RHFTextField
                name="ticketNumber"
                label="เลขที่ตั๋ว"
                placeholder="ระบุเลขที่ตั๋ว"
                InputLabelProps={{ shrink: true }}
              />
            </Stack>

            <RHFTextField
              multiline
              rows={3}
              name="note"
              label="หมายเหตุ"
              placeholder="ระบุหมายเหตุ"
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={beforeClose} disabled={isSubmitting}>
            ยกเลิก
          </Button>
          <LoadingButton variant="contained" type="submit" loading={isSubmitting}>
            บันทึก
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
